/**
 * InfinitSales - WorkspaceModeModal + auto-entrada
 *
 * Dois comportamentos da experiência "estar no workspace de outra pessoa":
 *
 * 1) AUTO-ENTRADA (quando NÃO está delegado): se o usuário marcou, em uma
 *    visita anterior, "entrar sempre neste workspace ao logar", ao abrir o app
 *    já delegamos automaticamente para aquele workspace — ele não precisa ir
 *    em Empresa toda vez. Guardado em localStorage por ator.
 *
 * 2) BOAS-VINDAS (quando ESTÁ delegado): modal one-time, no mesmo estilo do
 *    MemberWelcomeModal, explicando que ele está operando o workspace de
 *    alguém, com um checkbox PRÉ-MARCADO "entrar sempre neste workspace ao
 *    logar". Desmarcar remove a preferência de auto-entrada.
 *
 * Montado globalmente no App.js, dentro do AuthProvider.
 */
import React, { useEffect, useState, useRef } from 'react';
import { Users, Check } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import companyService from '../services/global/companyService';
import { getDelegation, isDelegated, startDelegation } from '../utils/delegation';

const autoKey = (actorId) => `infinitsales-autoworkspace-${actorId}`;
// Flag de SESSÃO (não permanente) por token de delegação: o modal aparece uma
// vez por ENTRADA no workspace. Reentrar gera um novo token → mostra de novo,
// para o usuário sempre poder reconfigurar a preferência. Navegar/recarregar
// dentro da mesma entrada (mesmo token) não repete.
const shownKey = (delegatedToken) => `workspace-modal-shown-${delegatedToken}`;

const WorkspaceModeModal = () => {
  const { colors } = useTheme();
  const { user, isCompanyAdmin, isCompanyMember } = useAuth();
  const [open, setOpen] = useState(false);
  const [keepEntering, setKeepEntering] = useState(true); // pré-marcado
  const autoTriedRef = useRef(false);

  const delegation = getDelegation();
  const delegated = isDelegated();

  // ----- 1) AUTO-ENTRADA (sessão NÃO delegada) -----
  useEffect(() => {
    if (delegated) return; // já está num workspace
    if (!user?.id) return;
    if (!(isCompanyAdmin || isCompanyMember)) return;
    if (autoTriedRef.current) return;

    // Só tenta uma vez por carregamento de página para não cair em loop se a
    // delegação falhar (alvo removido/suspenso).
    let pref = null;
    try {
      pref = localStorage.getItem(autoKey(user.id));
    } catch {}
    if (!pref || pref === user.id) return; // sem preferência ou aponta p/ si

    // Evita repetir a tentativa após um reload no mesmo "boot" lógico.
    const guard = `autoworkspace-tried-${user.id}`;
    try {
      if (sessionStorage.getItem(guard)) return;
      sessionStorage.setItem(guard, '1');
    } catch {}

    autoTriedRef.current = true;
    (async () => {
      try {
        const data = await companyService.enterWorkspace(pref);
        startDelegation(data);
        window.location.href = '/inbox';
      } catch {
        // Preferência inválida (alvo saiu da empresa): limpa para não insistir.
        try { localStorage.removeItem(autoKey(user.id)); } catch {}
      }
    })();
  }, [delegated, user, isCompanyAdmin, isCompanyMember]);

  // ----- 2) BOAS-VINDAS (sessão delegada) -----
  useEffect(() => {
    if (!delegated || !delegation) {
      setOpen(false);
      return;
    }
    const actorId = delegation.actorUserId;
    const targetId = delegation.targetUserId;
    const token = delegation.delegatedToken;
    if (!actorId || !targetId) return;

    // Preferência atual de auto-entrada deste ator.
    let pref = null;
    try { pref = localStorage.getItem(autoKey(actorId)); } catch {}

    // Se este workspace já é o default confirmado pelo usuário, não incomoda —
    // ele já decidiu que sempre entra aqui. O modal só aparece ao entrar num
    // workspace que NÃO é o default (entrada manual / novo). O checkbox vem
    // marcado por padrão para facilitar tornar este o novo default.
    const isConfirmedDefault = pref === targetId;
    setKeepEntering(true);

    // Já mostrado para ESTA entrada (mesmo token de delegação)? Não repete em
    // reloads/navegação. Nova entrada = novo token → mostra de novo.
    let shown = false;
    try {
      shown = token && sessionStorage.getItem(shownKey(token)) === 'true';
    } catch {}

    if (!isConfirmedDefault && !shown) setOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [delegated, delegation?.delegatedToken]);

  const handleAck = () => {
    const actorId = delegation?.actorUserId;
    const targetId = delegation?.targetUserId;
    const token = delegation?.delegatedToken;
    if (actorId && targetId) {
      try {
        // Não reabrir nesta mesma entrada (sobrevive a reload, some no logout).
        if (token) sessionStorage.setItem(shownKey(token), 'true');
        if (keepEntering) {
          localStorage.setItem(autoKey(actorId), targetId);
        } else {
          // Desmarcou: remove a auto-entrada se apontava para este alvo.
          if (localStorage.getItem(autoKey(actorId)) === targetId) {
            localStorage.removeItem(autoKey(actorId));
          }
        }
      } catch {}
    }
    setOpen(false);
  };

  if (!open) return null;

  const styles = {
    backdrop: {
      position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.65)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 2100, padding: '24px',
    },
    modal: {
      backgroundColor: colors.bgPrimary,
      border: '2px solid #f59e0b',
      borderRadius: '20px', maxWidth: '480px', width: '100%',
      overflow: 'hidden', boxShadow: '0 16px 60px rgba(0,0,0,0.55)',
    },
    hero: {
      padding: '24px',
      background: 'linear-gradient(135deg, #f59e0b30 0%, #f59e0b10 100%)',
      borderBottom: `1px solid ${colors.borderColor}`, textAlign: 'center',
    },
    iconWrap: {
      width: 56, height: 56, borderRadius: 16, backgroundColor: '#f59e0b',
      color: 'white', display: 'flex', alignItems: 'center',
      justifyContent: 'center', margin: '0 auto 12px auto',
    },
    title: { fontSize: 20, fontWeight: 700, color: colors.textPrimary, marginBottom: 4 },
    subtitle: { fontSize: 14, color: colors.textSecondary },
    body: { padding: '20px 24px' },
    list: { listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 12 },
    item: { display: 'flex', alignItems: 'flex-start', gap: 10, fontSize: 14, color: colors.textPrimary },
    checkIcon: {
      width: 22, height: 22, borderRadius: '50%', backgroundColor: '#f59e0b25',
      color: '#f59e0b', display: 'flex', alignItems: 'center',
      justifyContent: 'center', flexShrink: 0, marginTop: 1,
    },
    keepRow: {
      display: 'flex', alignItems: 'flex-start', gap: 10, marginTop: 18,
      padding: '12px', borderRadius: 12, backgroundColor: colors.bgTertiary,
      cursor: 'pointer', userSelect: 'none',
    },
    checkbox: (checked) => ({
      width: 20, height: 20, borderRadius: 6, flexShrink: 0, marginTop: 1,
      border: `2px solid ${checked ? '#f59e0b' : colors.borderColor}`,
      backgroundColor: checked ? '#f59e0b' : 'transparent',
      color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center',
      transition: 'all .15s',
    }),
    keepText: { fontSize: 13, color: colors.textPrimary, lineHeight: 1.45 },
    footer: { display: 'flex', justifyContent: 'flex-end', padding: '12px 24px 20px 24px' },
    cta: {
      display: 'inline-flex', alignItems: 'center', gap: 8, padding: '10px 20px',
      border: 'none', borderRadius: 10, backgroundColor: '#f59e0b',
      color: 'white', fontSize: 14, fontWeight: 700, cursor: 'pointer',
    },
  };

  return (
    <div style={styles.backdrop}>
      <div style={styles.modal}>
        <div style={styles.hero}>
          <div style={styles.iconWrap}><Users size={28} /></div>
          <div style={styles.title}>Modo workspace</div>
          <div style={styles.subtitle}>
            Você está acessando o workspace de{' '}
            <strong>{delegation?.targetName || 'outro usuário'}</strong>.
          </div>
        </div>
        <div style={styles.body}>
          <ul style={styles.list}>
            <li style={styles.item}>
              <span style={styles.checkIcon}><Check size={12} /></span>
              <span>
                Você <strong>atende e responde</strong> os contatos deste
                workspace normalmente — tudo que você fizer fica registrado em
                seu nome.
              </span>
            </li>
            <li style={styles.item}>
              <span style={styles.checkIcon}><Check size={12} /></span>
              <span>
                Os outros membros veem em tempo real quando você responde, com
                a marca <strong>"Enviado por você"</strong>.
              </span>
            </li>
            <li style={styles.item}>
              <span style={styles.checkIcon}><Check size={12} /></span>
              <span>
                Sua conta, plano e agentes <strong>não aparecem aqui</strong> —
                pertencem ao dono do workspace.
              </span>
            </li>
          </ul>

          <div
            style={styles.keepRow}
            onClick={() => setKeepEntering((v) => !v)}
            role="checkbox"
            aria-checked={keepEntering}
          >
            <div style={styles.checkbox(keepEntering)}>
              {keepEntering && <Check size={13} />}
            </div>
            <div style={styles.keepText}>
              <strong>Entrar sempre neste workspace ao logar.</strong>
              <br />
              Assim você não precisa abrir "Minha Empresa" toda vez. Pode
              desmarcar aqui ou sair pelo menu quando quiser.
            </div>
          </div>
        </div>
        <div style={styles.footer}>
          <button style={styles.cta} onClick={handleAck}>
            Entendi
            <Check size={14} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default WorkspaceModeModal;
